/**
 * DocAnalyticsApi — intercepts TechDocs navigate events and forwards them to
 * the doc-analytics backend plugin for storage.
 *
 * Only `action === 'navigate'` events from pluginId `'techdocs'` are captured;
 * all other events are silently ignored. Errors are swallowed so analytics
 * never impacts the page experience.
 */

import type { AnalyticsApi, AnalyticsEvent, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

export class DocAnalyticsApi implements AnalyticsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  captureEvent(event: AnalyticsEvent): void {
    if (event.action !== 'navigate' || event.context.pluginId !== 'techdocs') {
      return;
    }
    // Fire-and-forget — never await, never throw
    this.sendEvent(event).catch(() => {});
  }

  private async sendEvent(event: AnalyticsEvent): Promise<void> {
    const entityRef =
      typeof event.context.entityRef === 'string'
        ? event.context.entityRef
        : this.parseEntityRefFromSubject(event.subject);

    if (!entityRef) return;

    const pagePath = this.extractPagePath(event.subject, entityRef);
    const baseUrl = await this.discoveryApi.getBaseUrl('doc-analytics');

    await this.fetchApi.fetch(`${baseUrl}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityRef, pagePath }),
    });
  }

  /**
   * TechDocs route: /docs/{namespace}/{kind}/{name}/{...page}
   * Parses out the entity ref from the URL if context.entityRef is not set.
   */
  private parseEntityRefFromSubject(subject: string): string | undefined {
    const match = subject.match(/\/docs\/([^/?#]+)\/([^/?#]+)\/([^/?#]+)/);
    if (!match) return undefined;
    const [, namespace, kind, name] = match;
    return `${kind}:${namespace}/${name}`;
  }

  /**
   * Strips the /docs/{namespace}/{kind}/{name} prefix from the pathname,
   * returning the page path relative to the docs root (e.g. '/overview').
   */
  private extractPagePath(subject: string, entityRef: string): string {
    const colonIdx = entityRef.indexOf(':');
    if (colonIdx === -1) return '/';
    const kind = entityRef.slice(0, colonIdx);
    const rest = entityRef.slice(colonIdx + 1);
    const slashIdx = rest.indexOf('/');
    if (slashIdx === -1) return '/';
    const namespace = rest.slice(0, slashIdx);
    const name = rest.slice(slashIdx + 1);
    const prefix = `/docs/${namespace}/${kind}/${name}`;
    const idx = subject.indexOf(prefix);
    if (idx === -1) return '/';
    const path = subject.slice(idx + prefix.length) || '/';
    return path.startsWith('/') ? path : `/${path}`;
  }
}
